import { useCallback, useEffect, useRef, useState } from 'react';
import { shouldDeliver, type RecorderErrorKind, type RecorderState } from './recorder-state';

export type { RecorderErrorKind, RecorderState } from './recorder-state';

export interface UseVoiceRecorderOptions {
  maxDurationMs: number;
  /**
   * Minimum recording duration in ms. Recordings shorter than this are
   * silently discarded — `onResult` is not called and no error fires.
   * Defaults to 0 (every recording reaches `onResult`).
   */
  minDurationMs?: number;
  /** `durationMs` is measured by the hook, from start to stop. */
  onResult: (blob: Blob, mimeType: string, durationMs: number) => Promise<void> | void;
  onError?: (kind: RecorderErrorKind) => void;
}

export interface UseVoiceRecorder {
  state: RecorderState;
  durationMs: number;
  remainingMs: number;
  start: () => Promise<void>;
  stop: () => void;
  /** Stops and throws the recording away: `onResult` is not called. */
  cancel: () => void;
}

const PREFERRED_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

function detectSupport(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

export function useVoiceRecorder(opts: UseVoiceRecorderOptions): UseVoiceRecorder {
  const [state, setState] = useState<RecorderState>(() =>
    detectSupport() ? 'idle' : 'unsupported',
  );
  const [durationMs, setDurationMs] = useState(0);

  const optsRef = useRef(opts);
  optsRef.current = opts;

  const mountedRef = useRef(true);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickerRef = useRef<number | null>(null);
  const autoStopRef = useRef<number | null>(null);
  const mimeRef = useRef<string>('');
  // Set when stop() is called while start() is still awaiting getUserMedia.
  // Once the await resolves, start() observes this flag and aborts cleanly.
  const pendingStopRef = useRef<boolean>(false);
  // Set by cancel(): the stop that follows throws the recording away.
  const cancelledRef = useRef<boolean>(false);

  const cleanup = useCallback(() => {
    if (tickerRef.current !== null) {
      window.clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    if (autoStopRef.current !== null) {
      window.clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  const start = useCallback(async () => {
    if (!detectSupport()) {
      setState('unsupported');
      return;
    }
    if (state !== 'idle') return;
    pendingStopRef.current = false;
    cancelledRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const isPermission =
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'SecurityError');
      optsRef.current.onError?.(isPermission ? 'permission' : 'unknown');
      return;
    }

    if (pendingStopRef.current) {
      // User released before getUserMedia resolved — abort without ever starting.
      stream.getTracks().forEach((t) => t.stop());
      pendingStopRef.current = false;
      return;
    }

    const mime = pickMimeType();
    mimeRef.current = mime;

    let recorder: MediaRecorder;
    try {
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      optsRef.current.onError?.('unknown');
      return;
    }

    streamRef.current = stream;
    recorderRef.current = recorder;
    chunksRef.current = [];
    startedAtRef.current = Date.now();

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = async () => {
      const durationMs = Date.now() - startedAtRef.current;
      const collectedMime = recorder.mimeType || mimeRef.current || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: collectedMime });
      cleanup();
      if (mountedRef.current) setDurationMs(0);

      const minMs = optsRef.current.minDurationMs ?? 0;
      const deliver = shouldDeliver({
        cancelled: cancelledRef.current,
        mounted: mountedRef.current,
        durationMs,
        minDurationMs: minMs,
      });
      if (!deliver) {
        // Cancelled, an accidental tap, or the screen is gone: thrown away silently.
        if (mountedRef.current) setState('idle');
        return;
      }

      if (mountedRef.current) setState('pending');
      try {
        await optsRef.current.onResult(blob, collectedMime, durationMs);
      } finally {
        if (mountedRef.current) setState('idle');
      }
    };
    recorder.onerror = () => {
      optsRef.current.onError?.('unknown');
      cleanup();
      if (mountedRef.current) {
        setDurationMs(0);
        setState('idle');
      }
    };

    recorder.start();
    setState('recording');
    setDurationMs(0);

    tickerRef.current = window.setInterval(() => {
      if (mountedRef.current) setDurationMs(Date.now() - startedAtRef.current);
    }, 200);

    autoStopRef.current = window.setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state === 'recording') {
        recorderRef.current.stop();
      }
    }, optsRef.current.maxDurationMs);
  }, [state, cleanup]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
      return;
    }
    // No recorder yet — start() is still awaiting getUserMedia. Mark so that
    // start() aborts cleanly once the await resolves.
    pendingStopRef.current = true;
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    stop();
  }, [stop]);

  const remainingMs = Math.max(0, opts.maxDurationMs - durationMs);

  return { state, durationMs, remainingMs, start, stop, cancel };
}
